import React, { useState, useEffect } from "react";
import { useAuth } from "../../../../hooks/useAuth";
import axios from "axios";
import { getEnvironmentInfo } from "../../../../utils/environment";
import {
  deleteCheckerAPI,
  updateWebUserAPI,
  getUserDetailAPI,
  getCheckerListAPI,
  getCheckerAssignedAdListAPI,
  getCheckerAdListAPI,
  assignCheckerAdAPI,
} from "../../../../api/index";
import "./CheckerManagement.css";

const CheckerManagement = () => {
  const { user, updateUserInfo, getUserInfo } = useAuth();
  const [checkers, setCheckers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(null); // 체커 ID 저장
  const [availableAds, setAvailableAds] = useState([]); // 배정 가능한 광고 목록
  const [assignedAds, setAssignedAds] = useState([]); // 배정된 광고 목록
  const [selectedAds, setSelectedAds] = useState([]); // 광고 연결 모달에서 선택된 광고
  const [filterText, setFilterText] = useState(""); // 배정 가능한 광고 필터 텍스트
  const [filterType, setFilterType] = useState("missionName"); // 필터 타입: missionName 또는 storeName
  const [availableAdsPage, setAvailableAdsPage] = useState(1); // 배정 가능한 광고 페이지
  const [availableAdsTotalPages, setAvailableAdsTotalPages] = useState(0); // 배정 가능한 광고 총 페이지
  const [availableAdsTotalItems, setAvailableAdsTotalItems] = useState(0); // 배정 가능한 광고 총 개수

  // 체커 생성 폼 상태
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    name: "",
    phone: "",
    email: "",
  });

  // 아이디 중복 확인 상태
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [checkedUsername, setCheckedUsername] = useState(""); // 중복확인한 아이디 저장
  const [usernameError, setUsernameError] = useState("");

  // 체커 목록 조회
  const loadCheckers = async () => {
    if (!user?.id || user?.role !== "AGENCY") {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const checkerIds = user?.checker_list || [];
      if (checkerIds.length > 0) {
        // 각 체커 ID마다 개별적으로 조회
        const checkerPromises = checkerIds.map((checkerId) =>
          getCheckerListAPI(checkerId)
        );
        const responses = await Promise.all(checkerPromises);
        
        // 각 응답에서 체커 정보 추출
        const checkerList = responses
          .map((response) => {
            if (response.data?.list && response.data.list.length > 0) {
              return response.data.list[0];
            }
            return null;
          })
          .filter((checker) => checker !== null);
        
        setCheckers(checkerList);
      } else {
        setCheckers([]);
      }
    } catch (err) {
      console.error("체커 목록 조회 실패:", err);
      setError(err.response?.data?.msg || "체커 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 배정 가능한 광고 목록 조회
  const loadAvailableAds = async (page = 1) => {
    if (!user?.id) {
      return;
    }

    try {
      const apiParams = {};
      
      // 필터 파라미터 추가
      if (filterText.trim()) {
        if (filterType === "missionName") {
          apiParams.missionName = filterText.trim();
        } else if (filterType === "storeName") {
          apiParams.storeName = filterText.trim();
        }
      }
      
      const response = await getCheckerAssignedAdListAPI(user.id, page, 20, apiParams);
      console.log("배정 가능한 광고 응답:", response.data);
      
      // API 응답 구조: { data: [...] } 또는 { data: { list: [...] } }
      const adsList = response.data?.data || response.data?.list || (Array.isArray(response.data) ? response.data : []);
      const paginationInfo = response.data?.pagination;
      
      console.log("배정 가능한 광고 목록:", adsList);
      setAvailableAds(adsList);
      
      // 페이징 정보 업데이트
      if (paginationInfo) {
        setAvailableAdsPage(paginationInfo.page || 1);
        const actualLimit = 20;
        const total = paginationInfo.total || adsList.length;
        const calculatedTotalPages = Math.ceil(total / actualLimit);
        setAvailableAdsTotalPages(calculatedTotalPages);
        setAvailableAdsTotalItems(total);
      }
    } catch (err) {
      console.error("배정 가능한 광고 목록 조회 실패:", err);
    }
  };

  // 배정된 광고 목록 조회
  const loadAssignedAds = async (checkerId) => {
    if (!checkerId) {
      return;
    }

    try {
      const response = await getCheckerAdListAPI(checkerId);
      console.log("배정된 광고 응답:", response.data);
      // API 응답 구조: { data: [...] } 또는 { data: { list: [...] } }
      const assignedList = response.data?.data || response.data?.list || (Array.isArray(response.data) ? response.data : []);
      console.log("배정된 광고 목록:", assignedList);
      setAssignedAds(assignedList);
      
      // 배정된 광고 ID 목록을 selectedAds에 설정
      const assignedAdIds = assignedList.map((ad) => ad.ad_id || ad.id);
      setSelectedAds(assignedAdIds);
    } catch (err) {
      console.error("배정된 광고 목록 조회 실패:", err);
    }
  };

  useEffect(() => {
    loadCheckers();
    loadAvailableAds();
  }, [user?.id]);

  // 광고 연결 모달이 열릴 때 배정된 광고 목록 로드
  useEffect(() => {
    if (isLinkModalOpen) {
      setFilterText(""); // 모달 열릴 때 필터 초기화
      setFilterType("missionName"); // 모달 열릴 때 필터 타입 초기화
      setAvailableAdsPage(1); // 모달 열릴 때 페이지 초기화
      loadAvailableAds(1);
      loadAssignedAds(isLinkModalOpen);
    } else {
      setAssignedAds([]);
      setSelectedAds([]);
      setFilterText(""); // 모달 닫을 때 필터 초기화
      setFilterType("missionName"); // 모달 닫을 때 필터 타입 초기화
      setAvailableAdsPage(1); // 모달 닫을 때 페이지 초기화
      setAvailableAdsTotalPages(0);
      setAvailableAdsTotalItems(0);
    }
  }, [isLinkModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // 필터 타입 변경 시 검색 실행
  useEffect(() => {
    if (isLinkModalOpen && filterText.trim()) {
      // 필터 타입 변경 시 첫 페이지로 리셋하고 API 재호출
      setAvailableAdsPage(1);
      loadAvailableAds(1);
    }
  }, [filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  // 아이디 중복 확인
  const checkUsernameAvailability = async () => {
    const trimmedUsername = createForm.username.trim();
    if (!trimmedUsername) {
      alert("아이디를 먼저 입력해주세요.");
      return;
    }
    if (trimmedUsername.length < 8) {
      alert("아이디는 8자 이상이어야 합니다.");
      return;
    }
    if (!/^[a-zA-Z0-9]+$/.test(trimmedUsername)) {
      alert("아이디는 영문과 숫자만 사용 가능합니다.");
      return;
    }

    try {
      const result = await getUserInfo(trimmedUsername);
      if (result.success && result.isAvailable) {
        setIsUsernameAvailable(true);
        setCheckedUsername(trimmedUsername); // 중복확인한 아이디 저장
        setUsernameError("");
        alert("사용 가능한 아이디입니다.");
      } else {
        setIsUsernameAvailable(false);
        setCheckedUsername(""); // 중복확인 실패 시 초기화
        setUsernameError(result.message || "중복된 아이디입니다.");
        alert(result.message || "중복 확인 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("중복 확인 오류:", error);
      setIsUsernameAvailable(false);
      setCheckedUsername(""); // 오류 시 초기화
      setUsernameError("중복 확인 중 오류가 발생했습니다.");
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };

  // 체커 생성
  const handleCreateChecker = async (e) => {
    e.preventDefault();

    if (!user?.id || user?.role !== "AGENCY") {
      return;
    }

    // 아이디 중복 확인 체크
    if (isUsernameAvailable !== true) {
      alert("아이디 중복 확인을 해주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // SignupMobilePage.js를 참고하여 체커 생성 (role: CHECKER)
      const apiData = {
        username: createForm.username.trim(),
        password: createForm.password.trim(),
        name: createForm.name.trim(),
        phone: createForm.phone.replace(/[^0-9]/g, "") || undefined,
        email: createForm.email.trim() || undefined,
        role: "CHECKER",
      };

      // signupAPI 대신 직접 axios 호출하여 응답 전체를 받음 (id 포함)
      const { base: BASE } = getEnvironmentInfo();
      
      let newCheckerId = null;
      
      try {
        const response = await axios.post(`${BASE}/web/admin/create/member`, apiData, {
          withCredentials: true,
        });
        
        // 응답에서 id 추출
        if (response.data?.id) {
          newCheckerId = response.data.id;
        } else {
          // id가 없으면 getUserDetailAPI로 조회
          const checkerInfoResponse = await getUserDetailAPI(
            apiData.username,
            "CHECKER"
          );
          
          if (
            checkerInfoResponse.data?.list &&
            checkerInfoResponse.data.list.length > 0
          ) {
            newCheckerId = checkerInfoResponse.data.list[0].id;
          }
        }
      } catch (apiError) {
        const errorMsg = apiError.response?.data?.msg || "서버 연결에 실패했습니다.";
        setError(errorMsg);
        return;
      }

      if (!newCheckerId) {
        throw new Error("생성된 체커 ID를 가져올 수 없습니다.");
      }

      console.log("생성된 체커 ID:", newCheckerId);
      console.log("현재 user:", user);
      console.log("현재 checker_list:", user?.checker_list);

      // AGENCY의 checker_list에 새 체커 ID 추가
      const currentCheckerList = user?.checker_list || [];
      const updatedCheckerList = [...currentCheckerList, newCheckerId];

      console.log("업데이트할 checker_list:", updatedCheckerList);
      console.log("업데이트할 데이터:", {
        id: user.id,
        checker_list: updatedCheckerList,
      });

      // updateWebUserAPI로 AGENCY의 checker_list 업데이트
      let updateSuccess = false;
      try {
        console.log("updateWebUserAPI 호출 시작...");
        const updateResponse = await updateWebUserAPI({
          id: user.id,
          checker_list: updatedCheckerList,
        });
        console.log("updateWebUserAPI 응답:", updateResponse);
        console.log("updateWebUserAPI 응답 상태:", updateResponse?.status);
        console.log("updateWebUserAPI 응답 데이터:", updateResponse?.data);
        
        // API 호출이 성공했는지 확인
        if (updateResponse?.status === 200) {
          updateSuccess = true;
          console.log("✅ updateWebUserAPI 성공!");
        } else {
          console.error("❌ updateWebUserAPI 실패 - 상태 코드:", updateResponse?.status);
          throw new Error(`업데이트 실패: ${updateResponse?.status}`);
        }
      } catch (updateError) {
        console.error("❌ updateWebUserAPI 에러:", updateError);
        console.error("❌ updateWebUserAPI 에러 응답:", updateError?.response);
        console.error("❌ updateWebUserAPI 에러 데이터:", updateError?.response?.data);
        const errorMsg = updateError?.response?.data?.msg || updateError?.message || "체커 목록 업데이트에 실패했습니다.";
        setError(errorMsg);
        alert(`체커 목록 업데이트 실패: ${errorMsg}`);
        throw updateError;
      }
      
      if (!updateSuccess) {
        throw new Error("체커 목록 업데이트에 실패했습니다.");
      }

      // 사용자 정보 업데이트 (checker_list 반영)
      // 약간의 지연을 주어 서버 업데이트가 완료되도록 함
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updateResult = await updateUserInfo();
      console.log("updateUserInfo 결과:", updateResult);
      
      if (!updateResult.success) {
        console.error("사용자 정보 업데이트 실패:", updateResult.message);
        // 에러가 발생해도 계속 진행 (이미 서버에는 업데이트됨)
      }
      
      // 업데이트된 사용자 정보 확인
      const updatedUser = updateResult.userInfo || user;
      console.log("updateUserInfo 완료 후 checker_list:", updatedUser?.checker_list);
      
      // 만약 updateUserInfo가 실패했거나 checker_list가 업데이트되지 않았다면
      // 수동으로 user 상태 업데이트 (임시 조치)
      if (!updatedUser?.checker_list?.includes(newCheckerId)) {
        console.warn("checker_list가 업데이트되지 않음. 수동 업데이트 시도...");
        // 이 경우는 updateUserInfo가 제대로 작동하지 않은 것이므로
        // 페이지 새로고침을 권장하거나, 수동으로 상태를 업데이트할 수 있습니다.
      }

      // 성공 시 체커 목록 새로고침
      await loadCheckers();
      
      // 폼 초기화
      setCreateForm({
        username: "",
        password: "",
        name: "",
        phone: "",
        email: "",
      });
      setIsUsernameAvailable(null);
      setCheckedUsername("");
      setUsernameError("");
      setIsCreateModalOpen(false);
      
      alert("체커가 성공적으로 생성되었습니다.");
    } catch (err) {
      console.error("체커 생성 실패:", err);
      setError(err.response?.data?.msg || err.message || "체커 생성에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 체커 삭제
  const handleDeleteChecker = async (checkerId) => {
    if (!user?.id || user?.role !== "AGENCY") {
      return;
    }

    if (!window.confirm("정말 이 체커를 삭제하시겠습니까?")) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteCheckerAPI(checkerId);
      
      // AGENCY의 checker_list에서 삭제된 체커 ID 제거
      const currentCheckerList = user?.checker_list || [];
      const updatedCheckerList = currentCheckerList.filter(
        (id) => id !== checkerId
      );

      // updateWebUserAPI로 AGENCY의 checker_list 업데이트
      await updateWebUserAPI({
        id: user.id,
        checker_list: updatedCheckerList,
      });

      // 사용자 정보 업데이트 (checker_list 반영)
      await updateUserInfo();
      
      // 성공 시 체커 목록 새로고침
      await loadCheckers();
      
      alert("체커가 성공적으로 삭제되었습니다.");
    } catch (err) {
      console.error("체커 삭제 실패:", err);
      setError(err.response?.data?.msg || "체커 삭제에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 광고 연결/해제
  const handleLinkAds = async (checkerId, adIds) => {
    if (!user?.id || user?.role !== "AGENCY" || !checkerId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 현재 배정된 광고 목록
      const currentAssignedAdIds = assignedAds.map((ad) => ad.ad_id || ad.id);
      
      // 새로 배정할 광고 (선택된 광고 중 현재 배정되지 않은 것)
      const adsToAssign = adIds.filter((adId) => !currentAssignedAdIds.includes(adId));
      
      // 해제할 광고 (현재 배정되었지만 선택되지 않은 것)
      const adsToUnassign = currentAssignedAdIds.filter((adId) => !adIds.includes(adId));

      // 배정 작업
      const assignPromises = adsToAssign.map((adId) =>
        assignCheckerAdAPI(checkerId, adId)
      );

      // 해제 작업 (checkerId를 null로 전송)
      const unassignPromises = adsToUnassign.map((adId) =>
        assignCheckerAdAPI(null, adId)
      );

      // 모든 작업 실행
      await Promise.all([...assignPromises, ...unassignPromises]);

      // 배정된 광고 목록 새로고침
      await loadAssignedAds(checkerId);
      
      setIsLinkModalOpen(null);
      setSelectedAds([]);
      alert("광고 배정이 성공적으로 완료되었습니다.");
    } catch (err) {
      console.error("광고 배정 실패:", err);
      setError(err.response?.data?.msg || "광고 배정에 실패했습니다.");
      alert(`광고 배정 실패: ${err.response?.data?.msg || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 광고 선택 토글
  const toggleAdSelection = (adId) => {
    setSelectedAds((prev) =>
      prev.includes(adId)
        ? prev.filter((id) => id !== adId)
        : [...prev, adId]
    );
  };

  if (!user || user.role !== "AGENCY") {
    return (
      <div className="checker-management-container">
        <div className="checker-management-error">
          체커 관리 기능은 AGENCY 권한이 필요합니다.
        </div>
      </div>
    );
  }

  return (
    <div className="checker-management-container">
      <div className="checker-management-header">
        <h2>체커 관리</h2>
        <button
          className="checker-management-create-btn"
          onClick={() => setIsCreateModalOpen(true)}
        >
          + 체커 추가
        </button>
      </div>

      {error && (
        <div className="checker-management-error-message">{error}</div>
      )}

      {loading ? (
        <div className="checker-management-loading">로딩 중...</div>
      ) : checkers.length === 0 ? (
        <div className="checker-management-empty">
          등록된 체커가 없습니다. 체커를 추가해주세요.
        </div>
      ) : (
        <div className="checker-management-list">
          {checkers.map((checker) => (
            <div key={checker.id} className="checker-management-item">
              <div className="checker-management-item-info">
                <h3>{checker.name || checker.username}</h3>
                <p>아이디: {checker.username}</p>
                {checker.phone && <p>전화번호: {checker.phone}</p>}
                {checker.email && <p>이메일: {checker.email}</p>}
                <p>상태: {checker.status === "ACTIVE" ? "활성" : "정지"}</p>
              </div>
              <div className="checker-management-item-actions">
                <button
                  className="checker-management-link-btn"
                  onClick={() => setIsLinkModalOpen(checker.id)}
                >
                  광고 연결
                </button>
                <button
                  className="checker-management-delete-btn"
                  onClick={() => handleDeleteChecker(checker.id)}
                >
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 체커 생성 모달 */}
      {isCreateModalOpen && (
        <div className="checker-management-modal-overlay">
          <div className="checker-management-modal">
            <div className="checker-management-modal-header">
              <h3>체커 추가</h3>
              <button
                className="checker-management-modal-close"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  // 모달 닫을 때 폼 초기화
                  setCreateForm({
                    username: "",
                    password: "",
                    name: "",
                    phone: "",
                    email: "",
                  });
                  setIsUsernameAvailable(null);
                  setCheckedUsername("");
                  setUsernameError("");
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateChecker} className="checker-management-form">
              <div className="checker-management-form-group">
                <label>아이디 *</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={createForm.username}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreateForm({ ...createForm, username: value });
                      
                      // 중복확인한 아이디와 현재 입력된 아이디가 다르면 초기화
                      const trimmedValue = value.trim();
                      if (checkedUsername && trimmedValue !== checkedUsername) {
                        setIsUsernameAvailable(null);
                        setCheckedUsername("");
                        setUsernameError("");
                      }
                    }}
                    required
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={checkUsernameAvailability}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    중복확인
                  </button>
                </div>
                {usernameError && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                    {usernameError}
                  </div>
                )}
                {isUsernameAvailable === true && (
                  <div style={{ color: "green", fontSize: "12px", marginTop: "4px" }}>
                    사용 가능한 아이디입니다.
                  </div>
                )}
              </div>
              <div className="checker-management-form-group">
                <label>비밀번호 *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, password: e.target.value })
                  }
                  required
                />
              </div>
              <div className="checker-management-form-group">
                <label>이름 *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="checker-management-form-group">
                <label>전화번호</label>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, phone: e.target.value })
                  }
                />
              </div>
              <div className="checker-management-form-group">
                <label>이메일</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, email: e.target.value })
                  }
                />
              </div>
              <div className="checker-management-form-actions">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="checker-management-cancel-btn"
                >
                  취소
                </button>
                <button 
                  type="submit" 
                  className="checker-management-submit-btn"
                  disabled={isUsernameAvailable !== true}
                >
                  생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 광고 연결 모달 */}
      {isLinkModalOpen && (
        <div className="checker-management-modal-overlay">
          <div className="checker-management-modal">
            <div className="checker-management-modal-header">
              <h3>광고 연결</h3>
              <button
                className="checker-management-modal-close"
                onClick={() => setIsLinkModalOpen(null)}
              >
                ×
              </button>
            </div>
            <div className="checker-management-link-content">
              <p className="checker-management-link-description">배정할 광고를 선택하세요:</p>
              {availableAds.length === 0 && assignedAds.length === 0 ? (
                <p className="checker-management-empty-message">배정 가능한 광고가 없습니다.</p>
              ) : (
                <>
                  {/* 배정된 광고 목록 */}
                  {assignedAds.length > 0 && (
                    <div className="checker-management-assigned-section">
                      <h4 className="checker-management-section-title">배정된 광고</h4>
                      <div className="checker-management-ads-list">
                        {assignedAds.map((ad) => {
                          const adId = ad.ad_id || ad.id;
                          return (
                            <label key={adId} className="checker-management-ad-item">
                              <input
                                type="checkbox"
                                checked={selectedAds.includes(adId)}
                                onChange={() => toggleAdSelection(adId)}
                              />
                              <span className="checker-management-ad-name">
                                {ad.missionName || ad.adName}
                              </span>
                              <span className="checker-management-ad-status">
                                {ad.status || "N/A"}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* 배정 가능한 광고 목록 */}
                  {availableAds.length > 0 && (
                    <div className="checker-management-available-section">
                      <div className="checker-management-filter-header">
                        <h4 className="checker-management-section-title">배정 가능한 광고</h4>
                        <div className="checker-management-filter-wrapper">
                          <select
                            className="checker-management-filter-type-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={{
                              padding: "8px 12px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              marginRight: "8px",
                              fontSize: "14px",
                              backgroundColor: "white",
                              cursor: "pointer",
                            }}
                          >
                            <option value="missionName">상품명</option>
                            <option value="storeName">스토어 이름</option>
                          </select>
                          <input
                            type="text"
                            className="checker-management-filter-input"
                            placeholder={
                              filterType === "missionName"
                                ? "상품명으로 찾기"
                                : "스토어 이름으로 찾기"
                            }
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                setAvailableAdsPage(1);
                                loadAvailableAds(1);
                              }
                            }}
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setAvailableAdsPage(1);
                              loadAvailableAds(1);
                            }}
                            style={{
                              padding: "8px 16px",
                              backgroundColor: "#3b82f6",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                          >
                            검색
                          </button>
                        </div>
                      </div>
                      <div className="checker-management-ads-list">
                        {availableAds.length === 0 ? (
                          <div className="checker-management-no-results">
                            검색 결과가 없습니다.
                          </div>
                        ) : (
                          availableAds.map((ad) => {
                            const adId = ad.ad_id || ad.id;
                            return (
                              <label key={adId} className="checker-management-ad-item">
                                <input
                                  type="checkbox"
                                  checked={selectedAds.includes(adId)}
                                  onChange={() => toggleAdSelection(adId)}
                                />
                                <span className="checker-management-ad-name">
                                  {ad.missionName || ad.adName}
                                </span>
                                <span className="checker-management-ad-status">
                                  {ad.status || "N/A"}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {/* 페이징 컨트롤 */}
                      {availableAdsTotalPages > 1 && (
                        <div className="checker-management-pagination">
                          <div className="checker-management-pagination-info">
                            <span>총 {availableAdsTotalItems}개</span>
                          </div>
                          <div className="checker-management-pagination-controls">
                            <button
                              type="button"
                              onClick={() => {
                                const newPage = availableAdsPage - 1;
                                if (newPage >= 1) {
                                  setAvailableAdsPage(newPage);
                                  loadAvailableAds(newPage);
                                }
                              }}
                              disabled={availableAdsPage === 1}
                              className="checker-management-pagination-btn"
                            >
                              &lt;
                            </button>
                            {Array.from({ length: Math.min(5, availableAdsTotalPages) }, (_, i) => {
                              let pageNum;
                              if (availableAdsTotalPages <= 5) {
                                pageNum = i + 1;
                              } else if (availableAdsPage <= 3) {
                                pageNum = i + 1;
                              } else if (availableAdsPage >= availableAdsTotalPages - 2) {
                                pageNum = availableAdsTotalPages - 4 + i;
                              } else {
                                pageNum = availableAdsPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  type="button"
                                  className={`checker-management-pagination-btn ${
                                    availableAdsPage === pageNum ? "active" : ""
                                  }`}
                                  onClick={() => {
                                    setAvailableAdsPage(pageNum);
                                    loadAvailableAds(pageNum);
                                  }}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => {
                                const newPage = availableAdsPage + 1;
                                if (newPage <= availableAdsTotalPages) {
                                  setAvailableAdsPage(newPage);
                                  loadAvailableAds(newPage);
                                }
                              }}
                              disabled={availableAdsPage === availableAdsTotalPages}
                              className="checker-management-pagination-btn"
                            >
                              &gt;
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              <div className="checker-management-form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setIsLinkModalOpen(null);
                    setSelectedAds([]);
                    setFilterText("");
                    setFilterType("missionName");
                  }}
                  className="checker-management-cancel-btn"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={() => handleLinkAds(isLinkModalOpen, selectedAds)}
                  className="checker-management-submit-btn"
                  disabled={selectedAds.length === 0}
                >
                  연결 ({selectedAds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckerManagement;
